import React from 'react'
import {
  observable,
  computed,
  action
  // ObservableMap
} from 'mobx'
import {getTableMetrics} from './utils'
import SchemerModel from '../../core/SchemerModel'

type TableData = Map<string, SchemerModel>

export class TableStore {
  @observable data?: TableData  
  @observable stickyRowIds: string[] = []
  @observable stickyColumnKeys: string[] = []
  @observable enableRowHeader: boolean = true
  @observable dims = {
    height: 200,
    width: 300,
    scrollLeft: 0,
    scrollTop: 0
  }
  
  @computed
  get tableMetrics () {
    if (this.data) {
      return getTableMetrics(
        this.data,
        this.stickyRowIds,
        this.stickyColumnKeys,
        this.enableRowHeader
      )
    }
  }
  
  @computed
  get visibleCells () {
    if (this.tableMetrics) {
      const {
        scrollLeft,
        scrollTop,
        height,
        width          
      } = this.dims
      const {
        columnMetrics,
        rowMetrics,
        hashBounds,
        totalStickyWidth,
        totalStickyHeight,
        actualStuckRowIds,
        actualStuckColumnKeys
      } = this.tableMetrics

      const visibleCells = hashBounds.toArray({
        height: height,
        width: width,
        y: scrollTop,
        x: scrollLeft
      })
      
      const stuckTopRows : string[] = []
      const stuckBottomRows : string[] = []
      if (scrollTop > 0) {
        stuckTopRows.push('_header_row')
      }
      actualStuckRowIds.forEach(rowId => {
        const [,,,rowOffsetTop,,stickyRowOffsetTop] = rowMetrics[rowId] 
        if (scrollTop + stickyRowOffsetTop > rowOffsetTop) {
          stuckTopRows.unshift(rowId)
        }
        else if (scrollTop + height - totalStickyHeight + stickyRowOffsetTop <= rowOffsetTop) {
          stuckBottomRows.push(rowId)
        }
      })  

      const stuckLeftCols : string[] = []
      const stuckRightCols : string[] = []
      if (scrollLeft > 0) {
        stuckLeftCols.push('_header_col')
      }
      actualStuckColumnKeys.forEach(colKey => {
        const [,,,colOffsetLeft,,stickyColOffsetLeft] = columnMetrics[colKey] 
        if (scrollLeft + stickyColOffsetLeft > colOffsetLeft) {
          stuckLeftCols.push(colKey)
        }
        else if (scrollLeft + width - totalStickyWidth + stickyColOffsetLeft <= colOffsetLeft) {
          stuckRightCols.push(colKey)
        }
      })  
      
      
      const visibleRowIds : Set<string> = new Set(actualStuckRowIds)
      const visibleColKeys : Set<string> = new Set(actualStuckColumnKeys)
      // Add all scroll-area visible cells
      for (let ci = 0, cellCt = visibleCells.length; ci < cellCt; ci++) {
        const cell = visibleCells[ci]
        visibleRowIds.add(cell.rowId)
        visibleColKeys.add(cell.columnKey)
      }

      return {
        visibleRowIds: Array.from(visibleRowIds.values()),
        visibleColKeys: Array.from(visibleColKeys.values()),
        stuckTopRows,
        stuckBottomRows,
        stuckLeftCols,
        stuckRightCols
      }
    }
  }

  @action
  setDims = (height: number, width: number) => {
    this.dims.height = height
    this.dims.width = width
  } 
  
  @action
  setScroll = (scrollTop: number, scrollLeft: number) => {
    this.dims.scrollTop = scrollTop
    this.dims.scrollLeft = scrollLeft
  } 

  @action
  toggleStickyRow = (rowId: string) => {
    const currentIdx = this.stickyRowIds.indexOf(rowId)
    if (currentIdx === -1) {
      this.stickyRowIds.push(rowId)
    }
    else {
      this.stickyRowIds.splice(currentIdx, 1) // Pull      
    }
    if (this.tableMetrics) {
      const {rowMetrics} = this.tableMetrics
      // Rows must be rendered in original order for position:sticky to behave properly
      this.stickyRowIds = this.stickyRowIds.slice().sort((a, b) => rowMetrics[a][5] - rowMetrics[b][5])
    }
  }

  @action
  toggleStickyColumn = (columnKey: string) => {
    const currentIdx = this.stickyColumnKeys.indexOf(columnKey)
    if (currentIdx === -1) {
      this.stickyColumnKeys.push(columnKey)
    }
    else {
      this.stickyColumnKeys.splice(currentIdx, 1) // Pull      
    }
    if (this.tableMetrics) {
      const {columnMetrics} = this.tableMetrics
      // Columns must be rendered in original order for position:sticky to behave properly
      this.stickyColumnKeys = this.stickyColumnKeys.slice().sort((a, b) => columnMetrics[a][5] - columnMetrics[b][5])
    }
  }

  @action setData = (data: TableData) => {
    this.data = data
  }
}

export const TableStoreContext = React.createContext<TableStore>(new TableStore())